import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { createOrg, getOrg, listOrgs, updateOrg, type OrgRow } from '../../api/orgApi.js';
import { Field, FormShell, SaveBar, SelectField, todayIso } from './orgKit.js';

export function OrganizationFormPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId: string }>();
  const editing = Boolean(orgId);

  const [orgCode, setOrgCode] = useState('');
  const [orgName, setOrgName] = useState('');
  const [parentOrgId, setParentOrgId] = useState('');
  const [hierarchyLevel, setHierarchyLevel] = useState('1');
  const [description, setDescription] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());
  const [parents, setParents] = useState<OrgRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listOrgs(token).then((r) => setParents(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    if (!orgId) return;
    getOrg(token, orgId)
      .then((o) => {
        setOrgCode(o.orgCode);
        setOrgName(o.orgName);
        setParentOrgId(o.parentOrgId ?? '');
        setHierarchyLevel(String(o.hierarchyLevel ?? 1));
        setDescription(o.description ?? '');
        setEffectiveFromDate(o.effectiveFromDate ?? todayIso());
      })
      .catch((e) => setError((e as Error).message));
  }, [token, orgId]);

  async function submit(): Promise<void> {
    if (!orgName.trim()) { setError(t('org.organization.form.nameReq')); return; }
    if (!editing && !orgCode.trim()) { setError(t('org.organization.form.codeReq')); return; }
    setError(null);
    setBusy(true);
    try {
      if (editing && orgId) {
        await updateOrg(token, orgId, {
          orgName: orgName.trim(),
          parentOrgId: parentOrgId || undefined,
          hierarchyLevel: Number(hierarchyLevel) || 1,
          description: description.trim() || undefined,
        });
      } else {
        await createOrg(token, {
          orgCode: orgCode.trim(),
          orgName: orgName.trim(),
          parentOrgId: parentOrgId || undefined,
          hierarchyLevel: Number(hierarchyLevel) || 1,
          description: description.trim() || undefined,
          effectiveFromDate,
        });
      }
      navigate('/org/organizations');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const parentOptions = parents
    .filter((p) => p.orgId !== orgId)
    .map((p) => ({ value: p.orgId, label: `${p.orgCode} — ${p.orgName}` }));

  return (
    <FormShell
      label="organization-form"
      title={editing ? t('org.organization.form.editTitle') : t('org.organization.form.newTitle')}
      error={error}
      onSubmit={submit}
    >
      <Field label={t('org.field.code')} name="orgCode" value={orgCode} onChange={setOrgCode} required placeholder={t('org.organization.form.codePh')} />
      <Field label={t('org.field.name')} name="orgName" value={orgName} onChange={setOrgName} required />
      <SelectField label={t('org.field.parentOrg')} name="parentOrgId" value={parentOrgId} onChange={setParentOrgId} options={parentOptions} placeholder={t('org.common.noneTop')} />
      <Field label={t('org.field.hierarchyLevel')} name="hierarchyLevel" value={hierarchyLevel} onChange={setHierarchyLevel} type="number" />
      <Field label={t('org.field.description')} name="description" value={description} onChange={setDescription} />
      {!editing && (
        <Field label={t('org.field.effectiveFrom')} name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
      )}
      <SaveBar busy={busy} saveLabel={editing ? t('org.common.saveChanges') : t('org.organization.form.create')} cancelTo="/org/organizations" />
    </FormShell>
  );
}
